import type { DataFile } from '../../../../../services/dataHubService.interface.js';

export const ANNOTATION_EDITOR_PROPERTY = 'annotationEditor';
export const ANNOTATION_METADATA_VERSION = 1;
export const ANNOTATION_DRAFT_STATUS = 'Draft';

export interface AnnotationDocumentMetadata {
  formatVersion: number;
  documentId: string;
  kind: string;
  schemaId: string;
  sourceDataId: string;
  sourceFileId: string;
  sourceDatasetVersion: number;
  sourceContentHash?: string;
  saveOperationId: string;
  supersedesFileId?: string;
}

export interface AnnotationTarget {
  dataSetId: string;
  sourceDataId: string;
  sourceFileId: string;
  sourceDatasetVersion: number;
  annotationSetId: string;
  draftVersion: number;
  annotationDataId: string;
  documentId: string;
  schemaId: string;
  kind: string;
  fileName: string;
  fileId?: string;
  /** Legacy files gain managed metadata only after an explicit resume/save. */
  legacy?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readDocumentMetadata(
  file: DataFile,
): AnnotationDocumentMetadata | undefined {
  const value = file.properties?.[ANNOTATION_EDITOR_PROPERTY];
  if (value === undefined) return undefined;
  if (
    !isRecord(value) ||
    value.formatVersion !== ANNOTATION_METADATA_VERSION ||
    ![
      'documentId',
      'kind',
      'schemaId',
      'sourceDataId',
      'sourceFileId',
      'saveOperationId',
    ].every((key) => typeof value[key] === 'string' && value[key] !== '') ||
    typeof value.sourceDatasetVersion !== 'number' ||
    !Number.isSafeInteger(value.sourceDatasetVersion) ||
    value.sourceDatasetVersion < 1 ||
    (value.supersedesFileId !== undefined &&
      typeof value.supersedesFileId !== 'string') ||
    (value.sourceContentHash !== undefined &&
      typeof value.sourceContentHash !== 'string')
  ) {
    throw new Error(
      `Invalid annotation document metadata: ${file.fileName ?? file.id}`,
    );
  }
  return {
    formatVersion: ANNOTATION_METADATA_VERSION,
    documentId: String(value.documentId),
    kind: String(value.kind),
    schemaId: String(value.schemaId),
    sourceDataId: String(value.sourceDataId),
    sourceFileId: String(value.sourceFileId),
    sourceDatasetVersion: value.sourceDatasetVersion,
    saveOperationId: String(value.saveOperationId),
    supersedesFileId: value.supersedesFileId,
    sourceContentHash: value.sourceContentHash,
  };
}

function sameSource(
  a: AnnotationDocumentMetadata,
  b: AnnotationDocumentMetadata,
): boolean {
  return (
    a.documentId === b.documentId &&
    a.schemaId === b.schemaId &&
    a.kind === b.kind &&
    a.sourceDataId === b.sourceDataId &&
    a.sourceFileId === b.sourceFileId &&
    a.sourceDatasetVersion === b.sourceDatasetVersion
  );
}

/** Reject divergent heads and cross-document predecessor references. */
export function discoverDocuments(files: readonly DataFile[]): DataFile[] {
  const managed = files.map((file) => ({
    file,
    metadata: readDocumentMetadata(file),
  }));
  const superseded = new Set<string>();
  const children = new Set<string>();
  for (const { file, metadata } of managed) {
    if (metadata?.supersedesFileId === undefined) continue;
    const predecessor = managed.find(
      (item) => item.file.id === metadata.supersedesFileId,
    );
    if (predecessor === undefined) continue;
    if (
      predecessor.file.id === file.id ||
      children.has(predecessor.file.id) ||
      (predecessor.metadata !== undefined
        ? !sameSource(metadata, predecessor.metadata)
        : metadata.documentId !== predecessor.file.id)
    ) {
      throw new Error(
        'Conflicting annotation replacements. Resolve them before editing or publishing.',
      );
    }
    children.add(predecessor.file.id);
    superseded.add(predecessor.file.id);
  }
  const heads = managed.filter(({ file }) => !superseded.has(file.id));
  const ids = heads.flatMap(({ metadata }) =>
    metadata === undefined ? [] : [metadata.documentId],
  );
  if (
    new Set(ids).size !== ids.length ||
    (heads.length === 0 && files.length > 0)
  ) {
    throw new Error('Conflicting annotation document heads.');
  }
  // A cycle may coexist with an otherwise valid head.
  for (const { file } of managed) {
    const visited = new Set<string>();
    let current: DataFile | undefined = file;
    while (current !== undefined) {
      if (visited.has(current.id))
        throw new Error('Cyclic annotation replacement history.');
      visited.add(current.id);
      const id: string | undefined =
        readDocumentMetadata(current)?.supersedesFileId;
      current =
        id === undefined
          ? undefined
          : files.find((candidate) => candidate.id === id);
    }
  }
  return heads.map(({ file }) => file);
}

export function assertTargetFile(
  target: AnnotationTarget,
  file: DataFile,
): void {
  const metadata = readDocumentMetadata(file);
  if (metadata === undefined && target.legacy && file.id === target.documentId)
    return;
  if (
    metadata?.documentId !== target.documentId ||
    metadata.schemaId !== target.schemaId ||
    metadata.kind !== target.kind ||
    metadata.sourceDataId !== target.sourceDataId ||
    metadata.sourceFileId !== target.sourceFileId ||
    metadata.sourceDatasetVersion !== target.sourceDatasetVersion
  ) {
    throw new Error(
      'The selected annotation file does not match this source, schema, or document.',
    );
  }
}
